import { format, isFuture, isPast, isToday } from 'date-fns';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { extractErrorMessage } from 'ravenjs/utils/error';
import { formatDate } from 'ravenjs/utils/date';

import { BANNER_V3_LIST_TYPES, BANNERS_STATUS } from 'constants/banners';
import MESSAGES from 'constants/messages';
import { MODALS } from 'constants/modals';
import { getTimeStampFromDateTimeMeridian } from './date';

/**
 * Format banners list for data view.
 *
 * @param   {Array}   banners
 * @return  {Array}  formatted data
 */
export const formatBannersList = banners =>
    banners.map(banner => {
        const { active, effectiveFrom, effectiveTill } = banner;

        return {
            ...banner,
            from: formatDate(effectiveFrom, 'MM/DD/YYYY'),
            until: formatDate(effectiveTill, 'MM/DD/YYYY'),
            active: active ? 'Yes' : 'No',
        };
    });

/**
 * Normalize a v3 `bannerType` (e.g. "Attention Required") into the key
 * consumed by `BannerTypeCell` (e.g. "attention_required"). Falls back to
 * `announcement` when the value is missing.
 *
 * @param   {string}  bannerType
 * @return  {string}
 */
export const normalizeBannerType = (bannerType = '') =>
    String(bannerType)
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_') || 'announcement';

/**
 * Matches the rich text message's HTML markup, so it can be stripped down to
 * plain text. `<` is excluded from the tag body (alongside `>`) so an
 * unterminated `<` can't make the match restart from every following `<`,
 * which would scan the message once per `<` it contains.
 *
 * @type {RegExp}
 */
const HTML_TAG_REGEX = /<[^<>]+>/g;

/**
 * The HTML entities the rich text editor emits, each mapped onto the single
 * character it renders as.
 *
 * @type {Object}
 */
const HTML_ENTITIES = {
    '&nbsp;': ' ',
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
};

/**
 * Matches any of the `HTML_ENTITIES` keys.
 *
 * @type {RegExp}
 */
const HTML_ENTITY_REGEX = new RegExp(Object.keys(HTML_ENTITIES).join('|'), 'g');

/**
 * Strips a rich text message's HTML markup down to plain text, so it can be
 * shown in the banners list table without any formatting. Tags are replaced
 * with a space (rather than removed outright) so adjoining paragraphs don't
 * run into each other, then the extra whitespace this introduces is
 * collapsed back down.
 *
 * @param   {string}  html The rich text message.
 * @return  {string}       The plain text message.
 */
export const stripHtmlTags = (html = '') =>
    html
        .replace(HTML_TAG_REGEX, ' ')
        .replace(HTML_ENTITY_REGEX, entity => HTML_ENTITIES[entity])
        .replace(/\s+/g, ' ')
        .trim();

/**
 * Format a v3 banners list response into the row shape consumed by the
 * redesigned `BannerSectionTable` (see `BannerSectionTable/schema.json`).
 * `displayText`'s rich text HTML is stripped down to plain text for the
 * `message` column, so no markup leaks into the table.
 *
 * @param   {Array}   banners  v3 banner records
 * @return  {Array}   formatted rows
 */
export const formatBannersListV3 = (banners = []) =>
    banners.map(banner => {
        const {
            id,
            product,
            displayText,
            bannerType,
            effectiveFrom,
            effectiveTill,
            isActive,
            companies = [],
            skus = [],
        } = banner;

        return {
            id,
            product,
            bannerType: normalizeBannerType(bannerType),
            message: stripHtmlTags(displayText),
            startDateTime: effectiveFrom,
            endDateTime: effectiveTill,
            status: isActive ? 'active' : 'inactive',
            partnerRestricted: !isEmpty(companies),
            skuRestricted: !isEmpty(skus),
        };
    });

/**
 * Get banner status on basis of  `effectiveFrom` and `effectiveTill` dates.
 *
 * @param   {number}  effectiveFrom
 * @param   {number}  effectiveTill
 * @return  {string}
 */
export const getBannerStatus = ({ effectiveFrom, effectiveTill }) => {
    if (isFuture(effectiveFrom)) {
        return BANNERS_STATUS.UPCOMING;
    } else if (isPast(effectiveTill)) {
        return BANNERS_STATUS.EXPIRED;
    } else if (isPast(effectiveFrom) && isFuture(effectiveTill)) {
        return BANNERS_STATUS.IN_PROGRESS;
    }
    return BANNERS_STATUS.UNKNOWN;
};

/**
 * Format banner data for update banner form.
 *
 * @param {Object}      banner
 * @return {Object}
 */
export const formatBannerFormData = banner => {
    const {
        active,
        category,
        dashboardVisibility,
        dismissible,
        displayText,
        effectiveFrom = new Date(),
        effectiveTill = new Date(),
    } = banner;

    return {
        banner: {
            category,
            displayText,
        },
        bannerStatus: {
            active: active === 'Yes',
            dismissible,
            dashboardVisibility,
        },
        from: {
            time: format(effectiveFrom, 'h:00'),
            meridian: format(effectiveFrom, 'A'),
            date: formatDate(effectiveFrom, 'MM/DD/YYYY'),
        },
        until: {
            time: format(effectiveTill, 'h:00'),
            meridian: format(effectiveTill, 'A'),
            date: formatDate(effectiveTill, 'MM/DD/YYYY'),
        },
    };
};

/**
 * Create payload for update banner API.
 *
 * @param {Object}      bannerDetails
 * @return {Object}
 */
export const getUpdateBannerPayload = bannerDetails => {
    const {
        banner: { category = '', displayText = '' },
        bannerStatus: { active, dismissible, dashboardVisibility },
        from,
        id,
        until,
        visibleToRoles,
        ...rest
    } = bannerDetails;

    return {
        active,
        category,
        dismissible,
        displayText,
        effectiveFrom: getTimeStampFromDateTimeMeridian(from),
        effectiveTill: getTimeStampFromDateTimeMeridian(until),
        id,
        visibleToRoles,
        dashboardVisibility,
        ...rest,
    };
};

/**
 * Create payload for create banner API.
 *
 * @param {Object}      bannerDetails
 * @return {Object}
 */
export const getCreateBannerPayload = bannerDetails => {
    const {
        banner: { category = '', displayText = '' },
        bannerStatus: { active, dismissible, dashboardVisibility },
        from,
        id,
        until,
        visibleToRoles,
    } = bannerDetails;

    return {
        active,
        category,
        dismissible: Boolean(dismissible),
        displayText,
        effectiveFrom: getTimeStampFromDateTimeMeridian(from),
        effectiveTill: getTimeStampFromDateTimeMeridian(until),
        id,
        visibleToRoles,
        dashboardVisibility: Boolean(dashboardVisibility),
    };
};

/**
 * Form validator for date fields.
 *
 * @method              formDateValidator
 * @param {Array}       dates           date fields values
 * @param {Array}       errorPaths      paths to form error object
 * @param {Object}      errors          form errors
 * @return {*}
 */
export const formDateValidator = (dates, errorPaths, errors) => {
    dates.forEach((date, idx) => {
        const formattedDate = new Date(date).toLocaleDateString();
        if (!isToday(formattedDate) && !isFuture(formattedDate)) {
            get(errors, errorPaths[idx]).addError('Invalid Date');
        }
    });
    return errors;
};

/**
 * Get payload for dismiss banner API.
 * @param {Object}        banner
 * @return {{requestData: *, objectId: *}}
 */
export const getDismissBannerPayload = banner => {
    const { internalId } = banner;
    return {
        objectId: internalId,
        requestData: JSON.stringify(banner),
    };
};

/**
 * Fetch the live v3 banners and keep the ones that are active, not dismissed
 * and accepted by `isVisible`. Shared between the pages that render
 * `BannerPreview` banners (e.g. Dashboard, Ask an HR Expert, Handbooks), so
 * the fetch/filter logic doesn't need to be duplicated in each container.
 *
 * @param   {Function}  fetchBannersV3  the `fetchBannersV3` redux action
 * @param   {Function}  isVisible       predicate applied to each live banner
 * @return  {Promise<Array>}            the banners to render
 */
export const fetchLiveBanners = async (fetchBannersV3, isVisible) => {
    try {
        const response = await fetchBannersV3({ listType: BANNER_V3_LIST_TYPES.LIVE });

        return get(response, 'banners', []).filter(
            banner => banner.isActive && !banner.dismissed && isVisible(banner)
        );
    } catch {
        // Banners are non-critical to the pages that render them; fail
        // silently so a banner fetch error doesn't block the rest of the page.
        return [];
    }
};

/**
 * Fetch the live v3 banners for a given product (e.g. `Ask a Pro`,
 * `Handbooks`).
 *
 * @param   {Function}  fetchBannersV3  the `fetchBannersV3` redux action
 * @param   {string}    product         one of `PRODUCT_BANNER_TYPES`
 * @return  {Promise<Array>}            the active banners for the product
 */
export const fetchProductBanners = (fetchBannersV3, product) =>
    fetchLiveBanners(fetchBannersV3, banner => banner.product === product);

/**
 * Fetch the live v3 banners that are configured to show on the homepage,
 * regardless of the product they belong to.
 *
 * @param   {Function}  fetchBannersV3  the `fetchBannersV3` redux action
 * @return  {Promise<Array>}            the active banners for the homepage
 */
export const fetchDashboardBanners = fetchBannersV3 =>
    fetchLiveBanners(fetchBannersV3, banner => Boolean(banner.dashboardVisibility));

/**
 * Dismiss a v3 banner via `dismissBannerV3`, showing a loading indicator for
 * the duration of the call and an error modal if it fails. Shared between
 * the pages that render dismissible `BannerPreview` banners.
 *
 * @param   {Object}    params
 * @param   {Function}  params.dismissBannerV3  the `dismissBannerV3` redux action
 * @param   {Object}    params.banner           the banner being dismissed
 * @param   {Function}  params.isLoading        the `isLoading` redux action
 * @param   {Function}  params.openModal        the `openModal` redux action
 * @param   {Function}  [params.onSuccess]      called after a successful dismiss
 * @return  {Promise<void>}
 */
export const dismissProductBanner = async ({
    dismissBannerV3,
    banner,
    isLoading,
    openModal,
    onSuccess,
}) => {
    try {
        isLoading(true, MESSAGES.LOADING.DEFAULT);
        await dismissBannerV3({ bannerId: banner.id });
        if (onSuccess) {
            await onSuccess();
        }
    } catch (e) {
        openModal(MODALS.CONFIRM, {
            title: MESSAGES.MODAL.CONFIRM.TITLE.WARNING,
            description: extractErrorMessage(e),
        });
    } finally {
        isLoading(false);
    }
};
